<script setup lang="ts" generic="S extends string">
import { computed, ref, watch } from 'vue';

import { capitalCase } from 'change-case';
import { CollapsibleRoot, CollapsibleTrigger, VisuallyHidden } from 'reka-ui';
import { useI18n } from '@n8n/i18n';
import type { BaseTextKey } from '@n8n/i18n';

import {
	N8nAnimatedCollapsibleContent,
	N8nBadge,
	N8nCheckbox,
	N8nIcon,
	N8nIconButton,
	N8nInput,
	N8nInputLabel,
	N8nRadioGroup,
	N8nRadioGroupItem,
	N8nTooltip,
} from '@n8n/design-system';

import {
	DEFAULT_READ_SCOPE_ACTIONS,
	classifyScope,
	getReadOnlyScopes,
	groupScopes,
	inferSelectionMode,
} from './scopes.utils';
import type { ScopeGroup, ScopeGroupDefinition, ScopeSelectionMode } from './scopes.utils';

const props = withDefaults(
	defineProps<{
		modelValue: S[];
		availableScopes: S[];
		/** Maps `resource:` prefixes to display groups. */
		groups: readonly ScopeGroupDefinition[];
		/**
		 * i18n namespace for all selector texts. Expected keys under the prefix:
		 * `.label`, `.all`, `.readOnly`, `.custom`, `.count`, `.search.placeholder`,
		 * `.toggleGroup`, `.badge.read`, `.badge.write` and `.group.<groupKey>`.
		 */
		i18nKeyPrefix: string;
		rootTestId?: string;
		readActions?: readonly string[];
		disabled?: boolean;
		/**
		 * Tool names each scope unlocks. When provided, group rows show a tool
		 * count pill whose popover lists the tools enabled by the current
		 * selection. Expected i18n keys under the prefix: `.tools.count`,
		 * `.tools.enabled`, `.tools.enabledOf`, `.tools.notEnabled`.
		 */
		scopeTools?: Record<string, string[]>;
	}>(),
	{
		rootTestId: 'scopes-selector',
		readActions: () => DEFAULT_READ_SCOPE_ACTIONS,
		disabled: false,
	},
);

const emit = defineEmits<{
	'update:modelValue': [scopes: S[]];
}>();

const i18n = useI18n();

const searchTerm = ref('');
const expandedGroups = ref(new Set<string>());
const userPickedCustom = ref(false);

const mode = ref<ScopeSelectionMode>(
	inferSelectionMode(props.modelValue, props.availableScopes, props.readActions),
);

const treeExpanded = ref(mode.value === 'custom');

// Sync the radio to the selection except when the user has explicitly picked Custom —
// in that case we don't want a selection that happens to match "All" or "Read only"
// to silently flip the radio away from Custom.
watch(
	() => [props.modelValue, props.availableScopes],
	() => {
		const inferred = inferSelectionMode(props.modelValue, props.availableScopes, props.readActions);
		if (userPickedCustom.value && inferred !== 'custom') return;
		mode.value = inferred;
	},
);

// The tree is open only for Custom. Driving this off `mode` keeps it in sync whether
// the mode changed from a user radio click or a programmatic re-sync in the watch above.
// The header chevron can still override this for the current mode.
watch(mode, (newMode) => {
	treeExpanded.value = newMode === 'custom';
});

const isSearching = computed(() => searchTerm.value.trim() !== '');

const selectedSet = computed(() => new Set(props.modelValue));

const groupedScopes = computed(() => groupScopes(props.availableScopes, props.groups));

const filteredGroups = computed<Array<{ group: ScopeGroup<S>; visibleScopes: S[] }>>(() => {
	const term = searchTerm.value.trim().toLowerCase();

	if (!term) {
		return groupedScopes.value.map((group) => ({ group, visibleScopes: group.scopes }));
	}

	return groupedScopes.value
		.map((group) => ({
			group,
			visibleScopes: group.scopes.filter((scope) => scope.toLowerCase().includes(term)),
		}))
		.filter((entry) => entry.visibleScopes.length > 0);
});

function baseText(
	suffix: string,
	interpolate?: Record<string, string | number>,
	adjustToNumber?: number,
): string {
	return i18n.baseText(`${props.i18nKeyPrefix}.${suffix}` as BaseTextKey, {
		interpolate,
		adjustToNumber,
	});
}

function getGroupLabel(group: ScopeGroup<S>): string {
	if (group.isFallback) {
		return capitalCase(group.key);
	}
	return baseText(`group.${group.key}`);
}

function getBadgeLabel(scope: S): string {
	return classifyScope(scope, props.readActions) === 'read'
		? baseText('badge.read')
		: baseText('badge.write');
}

function emitScopes(scopes: S[]) {
	emit('update:modelValue', scopes);
}

type ScopeModeOption = {
	value: ScopeSelectionMode;
	label: string;
	description: string;
	'data-test-id': string;
};

const modeOptions = computed<ScopeModeOption[]>(() => [
	{
		value: 'all',
		label: baseText('all'),
		description: baseText('allDescription'),
		'data-test-id': 'scopes-mode-all',
	},
	{
		value: 'readOnly',
		label: baseText('readOnly'),
		description: baseText('readOnlyDescription'),
		'data-test-id': 'scopes-mode-read-only',
	},
	{
		value: 'custom',
		label: baseText('custom'),
		description: baseText('customDescription'),
		'data-test-id': 'scopes-mode-custom',
	},
]);

function onModeCardClick(value: ScopeSelectionMode) {
	if (props.disabled || value === mode.value) return;
	mode.value = value;
	onModeChange(value);
}

function onModeChange(newMode: string | undefined) {
	if (newMode === undefined) {
		return;
	}

	userPickedCustom.value = newMode === 'custom';

	if (newMode === 'all') {
		emitScopes([...props.availableScopes]);
	} else if (newMode === 'readOnly') {
		emitScopes(getReadOnlyScopes(props.availableScopes, props.readActions));
	} else if (newMode === 'custom') {
		emitScopes([]);
	}
}

function isGroupExpanded(group: ScopeGroup<S>): boolean {
	return isSearching.value || expandedGroups.value.has(group.key);
}

function toggleGroupExpanded(group: ScopeGroup<S>) {
	const expanded = new Set(expandedGroups.value);
	if (expanded.has(group.key)) {
		expanded.delete(group.key);
	} else {
		expanded.add(group.key);
	}
	expandedGroups.value = expanded;
}

// Per-group tool data: every tool the group unlocks plus which of those the
// current selection enables. Computed once per reactive change and read via
// O(1) lookups in the template, rather than rebuilding a Set on every call.
const groupToolData = computed(() => {
	const data = new Map<string, { tools: string[]; enabled: Set<string> }>();
	if (!props.scopeTools) return data;
	for (const group of groupedScopes.value) {
		const tools = new Set<string>();
		const enabled = new Set<string>();
		for (const scope of group.scopes) {
			for (const tool of props.scopeTools[scope] ?? []) {
				tools.add(tool);
				if (selectedSet.value.has(scope)) enabled.add(tool);
			}
		}
		data.set(group.key, { tools: [...tools], enabled });
	}
	return data;
});

function groupTools(group: ScopeGroup<S>): string[] {
	return groupToolData.value.get(group.key)?.tools ?? [];
}

function groupEnabledTools(group: ScopeGroup<S>): Set<string> {
	return groupToolData.value.get(group.key)?.enabled ?? new Set();
}

function isGroupChecked(group: ScopeGroup<S>): boolean {
	return group.scopes.every((scope) => selectedSet.value.has(scope));
}

function isGroupIndeterminate(group: ScopeGroup<S>): boolean {
	return !isGroupChecked(group) && group.scopes.some((scope) => selectedSet.value.has(scope));
}

function toggleGroup(group: ScopeGroup<S>, checked: boolean) {
	const selected = new Set(selectedSet.value);
	for (const scope of group.scopes) {
		if (checked) {
			selected.add(scope);
		} else {
			selected.delete(scope);
		}
	}
	emitScopes(props.availableScopes.filter((scope) => selected.has(scope)));
}

function toggleScope(scope: S, checked: boolean) {
	const selected = new Set(selectedSet.value);
	if (checked) {
		selected.add(scope);
	} else {
		selected.delete(scope);
	}
	emitScopes(props.availableScopes.filter((s) => selected.has(s)));
}
</script>

<template>
	<div :class="$style.selector" :data-test-id="rootTestId">
		<N8nInputLabel :label="baseText('label')" size="small" color="text-dark">
			<N8nRadioGroup
				v-model="mode"
				orientation="vertical"
				:disabled="disabled"
				:aria-label="baseText('label')"
				data-test-id="scopes-mode-radio"
				:class="$style.modes"
				@update:model-value="onModeChange"
			>
				<div
					v-for="option in modeOptions"
					:key="option.value"
					:class="[$style.modeCard, mode === option.value && $style.modeCardActive]"
					@click="onModeCardClick(option.value)"
				>
					<N8nRadioGroupItem
						:value="option.value"
						:label="option.label"
						:description="option.description"
						:disabled="disabled"
						:data-test-id="option['data-test-id']"
					/>
				</div>
			</N8nRadioGroup>
		</N8nInputLabel>

		<CollapsibleRoot v-model:open="treeExpanded" :class="$style.customSection">
			<CollapsibleTrigger as-child>
				<button type="button" :class="$style.treeHeader" data-test-id="scopes-tree-toggle">
					<N8nIcon :icon="treeExpanded ? 'chevron-down' : 'chevron-right'" size="small" />
					<span data-test-id="scopes-count">
						{{
							baseText('count', {
								selected: modelValue.length,
								total: availableScopes.length,
							})
						}}
					</span>
				</button>
			</CollapsibleTrigger>

			<N8nAnimatedCollapsibleContent blur>
				<div :class="$style.treeBody">
					<N8nInput
						v-model="searchTerm"
						size="small"
						clearable
						:placeholder="baseText('search.placeholder')"
						:aria-label="baseText('search.placeholder')"
						data-test-id="scopes-search"
					>
						<template #prefix>
							<N8nIcon icon="search" />
						</template>
					</N8nInput>

					<div :class="$style.groups">
						<div v-for="{ group, visibleScopes } in filteredGroups" :key="group.key">
							<div :class="$style.groupHeader">
								<N8nIconButton
									v-if="!isSearching"
									:icon="isGroupExpanded(group) ? 'chevron-down' : 'chevron-right'"
									variant="ghost"
									size="small"
									:aria-expanded="isGroupExpanded(group)"
									:aria-label="baseText('toggleGroup', { group: getGroupLabel(group) })"
									:data-test-id="`scope-group-toggle-${group.key}`"
									@click="toggleGroupExpanded(group)"
								/>
								<N8nCheckbox
									:model-value="isGroupChecked(group)"
									:indeterminate="isGroupIndeterminate(group)"
									:label="getGroupLabel(group)"
									:disabled="disabled"
									:data-test-id="`scope-group-${group.key}`"
									@update:model-value="(checked: boolean) => toggleGroup(group, checked)"
								/>
								<!-- `as-child` makes the pill itself the tooltip trigger, so keyboard
									focus opens the popover and `aria-describedby` lands on the focused
									element for screen readers. -->
								<N8nTooltip
									v-if="groupTools(group).length > 0"
									as-child
									placement="right"
									:show-after="150"
									:content-class="$style['tools-tooltip']"
								>
									<template #content>
										<div
											:class="$style['tools-popover']"
											:data-test-id="`scope-group-tools-popover-${group.key}`"
										>
											<div :class="$style['tools-popover-header']">
												{{
													baseText('tools.enabledOf', {
														enabled: groupEnabledTools(group).size,
														total: groupTools(group).length,
													})
												}}
											</div>
											<div
												v-for="tool in groupTools(group)"
												:key="tool"
												:class="[
													$style['tool-row'],
													{ [$style['tool-row-disabled']]: !groupEnabledTools(group).has(tool) },
												]"
											>
												<N8nIcon
													:icon="groupEnabledTools(group).has(tool) ? 'check' : 'circle'"
													size="xsmall"
													:class="$style['tool-icon']"
												/>
												<span :class="$style['tool-name']">{{ tool }}</span>
												<!-- State icons are aria-hidden; expose enabled state as text. -->
												<VisuallyHidden>
													{{
														groupEnabledTools(group).has(tool)
															? baseText('tools.enabled')
															: baseText('tools.notEnabled')
													}}
												</VisuallyHidden>
											</div>
										</div>
									</template>
									<span
										:class="$style['tools-tag']"
										tabindex="0"
										:data-test-id="`scope-group-tools-${group.key}`"
									>
										<N8nIcon icon="wrench" size="xsmall" />
										{{
											baseText(
												'tools.count',
												{ count: groupTools(group).length },
												groupTools(group).length,
											)
										}}
									</span>
								</N8nTooltip>
							</div>
							<CollapsibleRoot :open="isGroupExpanded(group)">
								<N8nAnimatedCollapsibleContent blur>
									<div :class="$style.scopeList">
										<div v-for="scope in visibleScopes" :key="scope" :class="$style.scopeRow">
											<N8nCheckbox
												:model-value="selectedSet.has(scope)"
												:label="scope"
												:disabled="disabled"
												:data-test-id="`scope-checkbox-${scope}`"
												@update:model-value="(checked: boolean) => toggleScope(scope, checked)"
											/>
											<N8nBadge
												:theme="
													classifyScope(scope, readActions) === 'read' ? 'default' : 'success'
												"
											>
												{{ getBadgeLabel(scope) }}
											</N8nBadge>
										</div>
									</div>
								</N8nAnimatedCollapsibleContent>
							</CollapsibleRoot>
						</div>
					</div>
				</div>
			</N8nAnimatedCollapsibleContent>
		</CollapsibleRoot>
	</div>
</template>

<style module lang="scss">
@use '@n8n/design-system/css/mixins/focus';

/* Option and checkbox labels render at 12px here, one step below the body copy. */
.selector {
	--radio-group-item--label--font-size: var(--font-size--2xs);
	--checkbox--label--font-size: var(--font-size--2xs);
}

.modes {
	display: grid;
	grid-template-columns: 1fr;
	gap: var(--spacing--2xs);
	margin-top: var(--spacing--xs);
}

.modeCard {
	padding: var(--spacing--2xs) var(--spacing--xs) var(--spacing--xs);
	border: var(--border);
	border-radius: var(--radius--lg);
	cursor: pointer;
}

.modeCardActive {
	background: var(--color--background--light-2);
	border-color: var(--color--primary);
}

.customSection {
	display: flex;
	flex-direction: column;
	margin-top: var(--spacing--xs);
}

/* Spacing lives inside the animated wrapper (as padding, not flex gap on the
   parent), so the collapse animates all the way to zero height with no jump. */
.treeBody {
	display: flex;
	flex-direction: column;
	gap: var(--spacing--2xs);
	padding-top: var(--spacing--2xs);
}

.treeHeader {
	display: flex;
	align-items: center;
	gap: var(--spacing--3xs);
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	color: var(--color--text);
	font-size: var(--font-size--xs);
	font-weight: var(--font-weight--medium);
	text-align: left;

	&:focus-visible {
		outline: var(--border--width--medium) solid var(--color--primary);
		outline-offset: 2px;
	}
}

.groups {
	display: flex;
	flex-direction: column;
	max-height: 320px;
	overflow-y: auto;
}

/* Compact rows (~24px pitch): the ghost toggle is the tallest thing in the row,
   so cap it instead of padding the whole row. */
.groupHeader {
	display: flex;
	align-items: center;
	gap: var(--spacing--3xs);
	min-height: var(--spacing--lg);

	> button:first-child {
		--button--height: var(--spacing--lg);
		width: var(--spacing--lg);
	}
}

.tools-tag {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing--4xs);
	margin-left: var(--spacing--2xs);
	padding: var(--spacing--5xs) var(--spacing--2xs);
	border-radius: var(--radius--full);
	border: var(--border);
	background-color: var(--color--background--light-2);
	color: var(--color--text--tint-1);
	font-size: var(--font-size--3xs);
	font-weight: var(--font-weight--medium);
	cursor: default;
	transition:
		color 150ms ease-out,
		border-color 150ms ease-out;

	&:hover,
	&:focus-visible {
		border-color: var(--color--primary);
		color: var(--color--text--shade-1);
	}

	@include focus.focus-visible-ring-offset;
}

/* the shared tooltip caps content at 180px and centers it; tool identifiers need more room */
:global(.n8n-tooltip).tools-tooltip {
	max-width: 320px;
	align-items: flex-start;
}

.tools-popover {
	display: flex;
	flex-direction: column;
	gap: var(--spacing--2xs);
	width: max-content;
	max-width: 100%;
	max-height: 320px;
	overflow-y: auto;
	padding: var(--spacing--4xs);
}

.tools-popover-header {
	font-size: var(--font-size--3xs);
	font-weight: var(--font-weight--bold);
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--color--text--tint-1);
	margin-bottom: var(--spacing--4xs);
}

.tool-row {
	display: flex;
	align-items: center;
	gap: var(--spacing--3xs);

	.tool-icon {
		color: var(--color--primary);
	}
}

.tool-row-disabled {
	.tool-icon,
	.tool-name {
		color: var(--color--text--tint-1);
	}
}

.tool-name {
	font-family: var(--font-family--monospace);
	font-size: var(--font-size--3xs);
}

.scopeList {
	display: flex;
	flex-direction: column;
	gap: var(--spacing--3xs);
	padding: var(--spacing--3xs) 0 var(--spacing--2xs) var(--spacing--2xl);
}

.scopeRow {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing--2xs);
}
</style>
